<template>
  <TransformTools
    ref="transformTools"
    v-model="modelValue"
    :trois-renderer="troisRenderer"
    :editor-mode="editorMode"
    :selection-list="selectionList"
    :has-selection="hasSelection"
    :view-ctrl="viewCtrl"
    :can-transform="canCurve && isCurvePlaneSelected"
    :can-disable-transform="true"
    :can-manual-transform="true"
    :can-toggle-pivot-mode="false"
    :select-object="selectObject"
    :history-list="historyList"
  />
  <div v-if="canCurve && activeCurve" class="level-item action" :class="{ active: isModifyTypeAdd }" @click="setCurveTool(CurveTool.add)">
    <el-tooltip :content="$t('components.mesh-editor.tooltip.curveAddPoint')" placement="bottom">
      <font-awesome-icon :icon="['fac', 'pen_plus']" />
    </el-tooltip>
  </div>
  <div v-if="canCurve && activeCurve" class="level-item action" :class="{ active: isModifyTypeRemove }" @click="setCurveTool(CurveTool.remove)">
    <el-tooltip :content="$t('components.mesh-editor.tooltip.curveRemovePoint')" placement="bottom">
      <font-awesome-icon :icon="['fac', 'pen_minus']" />
    </el-tooltip>
  </div>
  <div
    v-if="canCurve && activeCurve"
    class="level-item action"
    :class="{
      active: isModifyTypeMove,
    }"
    @click="setCurveTool(CurveTool.move)"
  >
    <el-tooltip :content="$t('components.mesh-editor.tooltip.curveMovePoint')" placement="bottom">
      <font-awesome-icon icon="pen-nib" />
    </el-tooltip>
  </div>
  <popover
    v-if="canCurve"
    ref="popoverAddCurve"
    :title="$t('components.mesh-editor.curve.title')"
    :has-selection="hasSelection"
    tooltip-key="addCurve"
    icon="circle-plus"
    icon-active="circle-minus"
    @show="showAddCurveSettings"
  >
    <el-form ref="dataForm" :model="curveData" @submit="submitCurve">
      <el-form-item prop="category" :label="$t('components.mesh-editor.curve.category')">
        <el-select v-model="curveData.category">
          <el-option v-for="category in allowedCurveCategories" :key="category" :value="category" :label="$t(`enum.curve-category.${category}`)" />
        </el-select>
      </el-form-item>
      <el-form-item
        prop="name"
        :rules="{
          required: true,
        }"
        :label="$t('components.mesh-editor.curve.name')"
      >
        <el-input v-model="curveData.name" />
      </el-form-item>
      <el-form-item prop="closed" :label="$t('components.mesh-editor.curve.closed')">
        <el-checkbox v-model="curveData.closed" />
      </el-form-item>
      <el-form-item prop="type" :label="$t('components.mesh-editor.curve.type')">
        <el-select v-model="curveData.type">
          <el-option v-for="cType in Object.values(CurveType)" :key="cType" :value="cType" :label="$t(`enum.curve-type.${cType}`)" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" :disabled="curveData.name.length === 0" @click="addPlaneCurve">
          {{ $t('components.mesh-editor.plane') }}
        </el-button>
        <el-button type="primary" :disabled="!canCreateMeshCurve || curveData.name.length === 0" @click="addMeshCurve">
          {{ $t('components.mesh-editor.mesh') }}
        </el-button>
      </el-form-item>
    </el-form>
  </popover>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-facing-decorator';
import { RendererPublicInterface } from 'troisjs';
import * as THREE from 'three';
import { DragControls } from 'three/examples/jsm/controls/DragControls.js';
import { ElForm } from 'element-plus';

import { CSG } from 'three-csg-ts';
import { MeshTreeData, MeshTreeDataItem } from '@/types/ui/MeshTreeData';
import { SelectionList } from '@/types/ui/SelectionList';
import { ViewCtrl } from '@/types/ui/ViewCtrl';
import { EditorMode } from '@/types/ui/EditorMode';
import * as THREEEnum from '@/types/enum/three';
import { SpecialObjectName, SpecialObjectPrefix } from '@/types/enum/three';
import { CurveTool } from '@/types/enum/editor';
import TransformTools from '@/components/three/tools/TransformTools.vue';
import { HistoryInfo, HistoryList, HistoryOperationType } from '@/types/ui/HistoryList';
import * as THREEBoundingBox from '@/utils/three/boundingBox';
import { CurveCategory } from '@/types/api/Model/Curve/CurveCategory';
import { CurveType } from '@/types/api/Model/Curve/CurveType';
import { CurveExportData } from '@/services/api/modelService';
import * as componentService from '@/services/api/componentService';
import { Vector3 } from '@/types/api/Utility/Vector3';
import { Curve } from '@/types/api/Model/Curve/Curve';
import Popover from '@/components/ui-elements/Popover.vue';

interface CurveData {
  curveId: number | null;
  category: CurveCategory;
  name: string;
  closed: boolean;
  type: CurveType;
}

interface CurveDescription {
  info: CurveData;
  target: THREE.Object3D;
  mesh: THREE.Object3D;
  curve: THREE.CatmullRomCurve3;
  geometry: THREE.BufferGeometry;
  curvePoints: THREE.Vector3[];
  curvePointsProjected: THREE.Vector3[];
  curvePointsProjectedBack: THREE.Vector3[];
  pointList: THREE.Object3D[];
  length: number;
  pointCount: number;
  projectionObject: THREE.Object3D | null;
  calculatedObject: THREE.Mesh | null;
  calculatedTreeData: MeshTreeDataItem | null;
  resultObject: THREE.Mesh | null;
  projectionDirection: number;
}

const minCurvePoints = 50;

@Component({
  components: { TransformTools, Popover },
})
/* eslint-disable @typescript-eslint/no-explicit-any*/

export default class CurveTools extends Vue implements HistoryInfo {
  //#region properties
  @Prop() modelValue!: MeshTreeData;
  @Prop() troisRenderer!: RendererPublicInterface;
  @Prop() editorMode!: EditorMode;
  @Prop() selectionList!: SelectionList;
  @Prop({ default: false }) hasSelection!: boolean;
  @Prop() viewCtrl!: ViewCtrl;
  @Prop({ default: true }) canCurve!: boolean;
  @Prop() readonly selectObject!: (target: THREE.Object3D | null, setSelectionFlag: boolean) => void;
  @Prop({ default: Object.values(CurveCategory) })
  allowedCurveCategories!: CurveCategory[];
  @Prop() historyList!: HistoryList;
  @Prop({ default: false }) canCalculateResult!: boolean;

  //history
  lastUpdateTime = -1;

  //curve
  curveList: CurveDescription[] = [];
  curveData: CurveData = {
    curveId: null,
    category: this.getDefaultCategory(),
    name: '',
    closed: false,
    type: CurveType.CENTRIPETAL,
  };
  ctrl_pt_geom!: THREE.SphereGeometry;
  ctrl_pt_material!: THREE.MeshLambertMaterial;

  //drag
  dragControls!: DragControls;
  dragObject: THREE.Object3D | null = null;
  dragCurve: CurveDescription | null = null;
  intersectionPoint: THREE.Vector3 | null = null;

  //tool
  curveTool: CurveTool = CurveTool.move;
  isModifyTypeAdd = false;
  isModifyTypeRemove = false;
  isModifyTypeMove = false;

  // transform
  transformTools: InstanceType<typeof TransformTools> | null = null;

  // enums for use in template section
  CurveType = CurveType;
  CurveTool = CurveTool;

  //#endregion properties

  //#region init
  mounted(): void {
    this.ctrl_pt_geom = new THREE.SphereGeometry(3);
    this.ctrl_pt_material = new THREE.MeshLambertMaterial({ color: 0x4d4dff });
    this.setupDrag();
    this.setCurveTool(CurveTool.move);

    this.troisRenderer.renderer.domElement.addEventListener('pointerdown', this.onPointerDown);
    this.troisRenderer.renderer.domElement.addEventListener('mousemove', this.onPointerMove);
    this.troisRenderer.renderer.domElement.addEventListener('pointerup', this.onPointerUp);

    setTimeout(() => {
      if (this.$refs.transformTools) {
        this.transformTools = this.$refs.transformTools as InstanceType<typeof TransformTools> ;
        this.transformTools.transformControl.addEventListener('mouseUp', this.onTransformMouseUp);
      }
    }, 100);
  }

  unmounted(): void {
    this.troisRenderer.renderer.domElement.removeEventListener('pointerdown', this.onPointerDown);
    this.troisRenderer.renderer.domElement.removeEventListener('mousemove', this.onPointerMove);
    this.troisRenderer.renderer.domElement.removeEventListener('pointerup', this.onPointerUp);
    if (this.transformTools) {
      this.transformTools.transformControl.removeEventListener('mouseUp', this.onTransformMouseUp);
    }

    this.ctrl_pt_geom?.dispose();
    this.ctrl_pt_material?.dispose();
  }

  @Watch('lastUpdateTime', { immediate: true })
  onDataChanged(): void {
    if (this.lastUpdateTime > -1) {
      const mesh = this.selectionList.getSelectedObject();
      this.modelValue.updateDB((mesh as any).apiData.uuid);
    }
  }

  canCreateMeshCurve = true;
  showAddCurveSettings(): void {
    const selection = this.selectionList.getSelectedObject();
    const apiData = (selection as any)?.apiData;
    this.canCreateMeshCurve =
      selection !== null && apiData !== null && !apiData.name.startsWith(SpecialObjectPrefix.plane) && !apiData.name.startsWith(SpecialObjectPrefix.curve);
  }

  private onTransformMouseUp = () => {
    this.saveTransform();
  };

  setCurveTool(type: CurveTool): void {
    if (this.dragObject) {
      this.dragEnd();
    }

    this.curveTool = type;
    switch (type) {
      case CurveTool.move:
        this.activateDrag();
        break;
      case CurveTool.add:
        this.deactivateDrag();
        break;
      case CurveTool.remove:
        this.deactivateDrag();
        break;
    }
    this.isModifyTypeMove = this.isCurveTool(CurveTool.move);
    this.isModifyTypeAdd = this.isCurveTool(CurveTool.add);
    this.isModifyTypeRemove = this.isCurveTool(CurveTool.remove);
    if (this.transformTools) {
      this.transformTools.onCanDisableTransformChanged();
    }
  }

  isCurveTool(type: CurveTool): boolean {
    if (this.curveTool) {
      return this.curveTool === type.toString();
    }
    return false;
  }

  canEditCurve(): boolean {
    if (this.transformTools) return this.canCurve && this.transformTools.isModifyTypeNone;
    return this.canCurve;
  }

  activeCurve: CurveDescription | null = null;
  oldCurvePoints: THREE.Vector3[] = [];
  curvePointsChanged = false;
  onPointerDown(event: MouseEvent): void {
    if (!this.canEditCurve()) {
      return;
    }

    const selection = this.selectionList.getSelectedObject();
    if (selection) {
      const selectedCurve = this.getCurveForLine(selection);
      if (selectedCurve) this.activeCurve = selectedCurve;
    }

    if (!this.activeCurve || !this.activeCurve.mesh.visible) {
      return;
    }
    
    this.oldCurvePoints = this.activeCurve.pointList.map((point) => point.position.clone());
    this.curvePointsChanged = false;

    switch (this.curveTool) {
      case CurveTool.move:
        // The move logic is now handled elsewhere or can be placed in its own function if needed.
        break;
      case CurveTool.add:
        this.handleAddCurvePoint(this.activeCurve, event);
        break;
      case CurveTool.remove:
        const clickPoint = this.selectionList.getClickedObject(event, false, SpecialObjectName.CurvePoint);
        if (clickPoint) {
          this.handleRemoveCurvePoint(this.activeCurve, clickPoint);
        }
        break;
    }
  }

  private handleAddCurvePoint(curve: CurveDescription, event: MouseEvent): void {
    if (!curve.target) {
      console.warn("No active curve target — cannot add point.");
      return;
    }

    const sceneHasTarget = this.troisRenderer.scene?.getObjectById(curve.target.id) !== undefined;
    if (!sceneHasTarget) {
      console.warn("Active curve target is no longer in scene — aborting point add.");
      return;
    }

    const intersection = this.selectionList.checkIntersection(event, false, curve.target);
    if (!(intersection instanceof THREE.Vector3)) {
      console.warn("No intersection with target — point not added.");
      return;
    }

    this.troisRenderer.scene?.updateMatrixWorld(true);
    const newPoint = this.getNewPointFromIntersection(intersection, curve.target, curve.mesh as THREE.Mesh);

    const lineIndex = this.isPointOnActiveCurve(newPoint);
    const newPointObjects = this.displayCurvePoints(curve.mesh, [newPoint]);

    if (lineIndex > -1) {
      curve.curve.points.splice(lineIndex, 0, newPoint);
      curve.pointList.splice(lineIndex, 0, ...newPointObjects);
    } else {
      curve.curve.points.push(newPoint);
      curve.pointList.push(...newPointObjects);
    }

    this.updateCurveGeometry(curve);
    this.curvePointsChanged = true;
  }

  private handleRemoveCurvePoint(curve: CurveDescription, clickPoint: THREE.Object3D): void {
    const clickIndex = curve.pointList.findIndex((item) => item.uuid === clickPoint.uuid);
    if (clickIndex === -1) {
      return;
    }

    this.removeFromDrag([clickPoint]);
    curve.curve.points.splice(clickIndex, 1);
    curve.pointList.splice(clickIndex, 1);
    
    // Dispose of the geometry and update the curve
    if (curve.geometry) {
      curve.geometry.dispose();
    }
    this.updateCurveGeometry(curve);

    const line = clickPoint.parent;
    if (line) {
      const linePointIndex = line.children.findIndex((item) => item.uuid === clickPoint.uuid);
      if (linePointIndex > -1) {
        line.children.splice(linePointIndex, 1);
      }
    }
    this.curvePointsChanged = true;
  }

  private updateCurveGeometry(curve: CurveDescription): void {
    const validPoints = curve.curve.points.filter(p => p instanceof THREE.Vector3);
    if (validPoints.length < 2) {
      console.warn("Need at least 2 points to form a curve.");
      return;
    }

    const pointsCount = Math.max(50, validPoints.length);
    const newPoints = curve.curve.getPoints(pointsCount);

    if (curve.geometry) {
      curve.geometry.dispose();
    }
    curve.geometry = new THREE.BufferGeometry().setFromPoints(newPoints);
    (curve.mesh as THREE.Mesh).geometry = curve.geometry;
    CurveTools.displayCurve(curve);
  }


  private getNewPointFromIntersection(intersection: THREE.Vector3, target: THREE.Object3D, curveMesh: THREE.Mesh): THREE.Vector3 {
    const planeWorldMatrix = target.matrixWorld;
    const curveParentInverseWorldMatrix = new THREE.Matrix4().copy(curveMesh.matrixWorld).invert();
    const newPoint = intersection.clone();
    newPoint.applyMatrix4(planeWorldMatrix);
    newPoint.applyMatrix4(curveParentInverseWorldMatrix);
    return newPoint;
  }

  private isPointOnActiveCurve(searchPoint: THREE.Vector3): number {
    const bbox = this.getSelectionBox();
    const measure = this.getBoxSize(bbox);
    const maxDistance = measure.x / 50;
    if (this.activeCurve && this.activeCurve.curve.points.length > 2) {
      let pointIndex = 0;
      const points = this.activeCurve.curve.getPoints(this.activeCurve.pointCount);
      for (let i = 1; i < points.length; i++) {
        const curvePoint = this.activeCurve.curve.points[pointIndex];
        const line = new THREE.Line3(points[i - 1], points[i]);
        const target: THREE.Vector3 = new THREE.Vector3();
        const distanceCurvePoint = line.closestPointToPoint(curvePoint, true, target).distanceTo(curvePoint);
        if (distanceCurvePoint < maxDistance && pointIndex < this.activeCurve.curve.points.length - 1) {
          pointIndex++;
        }
        const distanceSearchPoint = line.closestPointToPoint(searchPoint, true, target).distanceTo(searchPoint);
        if (distanceSearchPoint < maxDistance) return pointIndex;
      }
    }
    return -1;
  }

  snapPointCalculationIsRunning = false;
  onPointerMove(event: MouseEvent): void {
    if (!this.canEditCurve()) return;
    this.calculateSnapPoint(event);
  }

  async onPointerUp(event: MouseEvent): Promise<void> {
    if (this.dragObject) {
      this.dragEnd();
    }

    if (!this.canEditCurve()) return;
    await this.calculateSnapPoint(event);

    if (this.activeCurve?.target) {
      this.selectionList.selectObject(this.activeCurve.target);
    }
  }

  async calculateSnapPoint(event: MouseEvent): Promise<void> {
    if (!this.dragObject || !this.dragCurve) return;

    const selection = this.dragCurve.target;
    if (!selection) return;

    this.intersectionPoint = this.selectionList.checkIntersection(event, false, selection);
    if (!this.intersectionPoint) return;

    if (!this.activeCurve || !this.activeCurve.mesh || !this.activeCurve.mesh.matrixWorld) {
      console.warn("Aborting calculateSnapPoint: activeCurve or its mesh is not ready.");
      return;
    }

    this.troisRenderer.scene?.updateMatrixWorld(true);
    const planeWorldMatrix = this.activeCurve.target.matrixWorld;
    const curveParentInverseWorldMatrix = new THREE.Matrix4().copy(this.activeCurve.mesh.matrixWorld).invert();
    const localPos = this.intersectionPoint.clone();
    localPos.applyMatrix4(planeWorldMatrix);
    localPos.applyMatrix4(curveParentInverseWorldMatrix);

    this.dragObject.position.copy(localPos);
    this.dragMesh(this.dragObject);
  }
  //#endregion init

  //#region calculate size
  getSceneBox(): THREE.Box3 {
    return THREEBoundingBox.getSceneBox(this.modelValue.getMeshList());
  }

  getSelectionBox(): THREE.Box3 {
    return THREEBoundingBox.getSelectionBox(this.selectionList.getSelectedObject(), this.modelValue.getMeshList());
  }

  getSelectionSize(): THREE.Vector3 {
    return THREEBoundingBox.getSelectionSize(this.selectionList.getSelectedObject(), this.modelValue.getMeshList());
  }

  getPointRadius(): number {
    const measure = this.getSelectionSize();
    return measure.x / 50;
  }

  getBoxSize(bbox: THREE.Box3): THREE.Vector3 {
    const measure = new THREE.Vector3(0, 0, 0);
    bbox.getSize(measure);
    return measure;
  }

  getSceneSize(): THREE.Vector3 {
    const bbox = this.getSceneBox();
    return this.getBoxSize(bbox);
  }

  getBoxCenter(bbox: THREE.Box3): THREE.Vector3 {
    const center = new THREE.Vector3(0, 0, 0);
    bbox.getCenter(center);
    return center;
  }
  //#endregion calculate size

  //#region add
  async addPlaneCurve(): Promise<CurveDescription | null> {
    const selection = this.selectionList.getSelectedObject();
    const bbox = this.getSelectionBox();
    const measure = this.getBoxSize(bbox);
    this.ctrl_pt_geom = new THREE.SphereGeometry(this.getPointRadius());
    const center = this.getBoxCenter(bbox);
    const geometry = new THREE.PlaneGeometry(measure.x, measure.y);
    const alpha = 30;
    const material = new THREE.MeshBasicMaterial({
      color: 0x333333,
      side: THREE.DoubleSide,
      opacity: alpha / 100.0,
      transparent: true,
    });

    // Create the final object to add to the scene
    const plane = this.modelValue.addObject(
      `${SpecialObjectPrefix.plane}${this.curveData.name}`,
      THREEEnum.ObjectType.Mesh,
      geometry,
      material,
      new THREE.Vector3(center.x, center.y, center.z + measure.z * 0.55),
      new THREE.Euler(0, 0, 0),
      new THREE.Vector3(1, 1, 1),
      this.getItemCategory()
    );
    plane.opacity = alpha;
    return await this.addCurve(plane, plane, null, selection);
  }

  async addMeshCurve(): Promise<CurveDescription | null> {
    const selection = this.selectionList.getSelectedObject();
    if (selection) {
      this.ctrl_pt_geom = new THREE.SphereGeometry(this.getPointRadius() / selection.scale.x);
      return await this.addCurve(selection, this.getItemCategory(), selection, selection);
    }
    return null;
  }

  private getItemCategory(): MeshTreeDataItem {
    const curveCategory = (this as any).$t(`enum.curve-category.${this.curveData.category}`);
    let itemCategory = this.modelValue.getItemByName(curveCategory);
    if (!itemCategory) {
      itemCategory = this.modelValue.addCategory(curveCategory, (this as any).$t('enum.upload-category.curve'));
    }
    return itemCategory;
  }

  submitCurve(event: Event | null = null): void {
    if (event) event.preventDefault();
    const dataForm = this.$refs.dataForm as typeof ElForm;
    dataForm?.validate(async (valid: any) => {
      if (valid) {
        const selection = this.selectionList.getSelectedObject();
        if (selection) {
          await this.addMeshCurve();
        } else {
          await this.addPlaneCurve();
        }
      }
    });
  }

  async addCurve(
    target: THREE.Object3D | MeshTreeDataItem | null,
    treeParent: MeshTreeDataItem,
    parent: THREE.Object3D | null = null,
    projectionObject: THREE.Object3D | null = null
  ): Promise<CurveDescription | null> {
    const awaitTimeout = (delay: number | undefined) => new Promise((resolve) => setTimeout(resolve, delay));

    const curvePoints: THREE.Vector3[] = [];
    curvePoints.length = 0;
    const curve = new THREE.CatmullRomCurve3(curvePoints, this.curveData.closed, this.curveData.type);
    const points: THREE.Vector3[] = curvePoints.length > 1 ? curve.getPoints(minCurvePoints) : [];
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({ color: 0xff0000 });

    // Create the final object to add to the scene
    const group = this.modelValue.addObject(
      `${SpecialObjectPrefix.curve}${this.curveData.name}`,
      THREEEnum.ObjectType.Line,
      geometry,
      material,
      new THREE.Vector3(0, 0, 0),
      new THREE.Euler(0, 0, 0),
      new THREE.Vector3(1, 1, 1),
      treeParent
    );
    //this.modelValue.updateDB(selectionItem.uuid);
    this.modelValue.updateStructure();
    let curveDescription: CurveDescription | null = null;
    await awaitTimeout(500).then(() => {
      const list = this.displayCurvePoints(group.mesh, curvePoints);
      this.modelValue.selectItem(group, true, true);
      if (target) {
        const curveResult = {
          info: {
            curveId: this.curveData.curveId,
            category: this.curveData.category,
            name: this.curveData.name,
            closed: this.curveData.closed,
            type: this.curveData.type,
          },
          target: target instanceof MeshTreeDataItem ? target.mesh : target,
          mesh: group.mesh,
          curve: curve,
          geometry: geometry,
          curvePoints: curvePoints,
          curvePointsProjected: [],
          curvePointsProjectedBack: [],
          pointList: list,
          length: 0,
          pointCount: minCurvePoints,
          projectionObject: projectionObject,
          calculatedObject: null,
          calculatedTreeData: null,
          resultObject: null,
          projectionDirection: 1,
        };
        this.curveList.push(curveResult);
        curveDescription = curveResult;
        this.activeCurve = curveResult;
      }
      if (parent) parent.add(group.mesh);
      this.resetCurveData();
    });
    this.setCurveTool(CurveTool.add);

    const popoverAddCurve = this.$refs.popoverAddCurve as Popover;
    if (popoverAddCurve) popoverAddCurve.hide();

    const selection = this.selectionList.getSelectedObject();
    if (selection && this.modelValue.modelId) {
      const apiData = (selection as any)?.apiData;
      if (!apiData.componentId || !apiData.uuid) {
        console.warn("No API data found for the selected object.");
        return null;
      } else {
        componentService.updateCurves(
          this.modelValue.modelId,
          apiData.componentId,
          apiData.uuid,
          this.curveList.map((item) => {
            const bbox = item.geometry.boundingBox;
            const measure = bbox ? this.getBoxSize(bbox) : new THREE.Vector3(0, 0, 0);
            const data = {
              curveId: item.info.curveId,
              name: item.info.name,
              category: item.info.category.toUpperCase(),
              type: item.info.type.toUpperCase(),
              closed: item.info.closed,
              points: item.curvePoints.map((point) => {
                return Vector3.createFromThreeVector(point);
              }),
              position: Vector3.createFromThreeVector(item.target.position),
              rotation: Vector3.createFromThreeEuler(item.target.rotation),
              scale: Vector3.createFromThreeVector(item.target.scale),
            } as any;
            return data as Curve;
          })
        );
      }
    }
    return curveDescription;
  }

  resetCurveData(): void {
    this.curveData.curveId = null;
    this.curveData.category = this.getDefaultCategory();
    this.curveData.name = '';
    this.curveData.closed = false;
    this.curveData.type = CurveType.CENTRIPETAL;
  }
  //#endregion add

  //#region drag & drop
  activateDrag(): void {
    if (this.dragControls) this.dragControls.connect(this.troisRenderer.renderer.domElement);
  }

  deactivateDrag(): void {
    if (this.dragControls) this.dragControls.disconnect();
  }

  setupDrag(): void {
    if (this.troisRenderer && this.troisRenderer.camera) {
      this.dragControls = new DragControls([], this.troisRenderer.camera, this.troisRenderer.renderer.domElement);
    }

    this.dragControls.removeEventListener('drag', this.drag);
    this.dragControls.removeEventListener('dragstart', this.dragStart);
    this.dragControls.removeEventListener('dragend', this.dragEnd);

    this.dragControls.addEventListener('drag', this.drag);
    this.dragControls.addEventListener('dragstart', this.dragStart);
    this.dragControls.addEventListener('dragend', this.dragEnd);
  }

  addToDrag(objects: THREE.Object3D[]): void {
    const dragObjects = this.dragControls.objects;
    for (const object of objects) {
      const index = dragObjects.findIndex((item) => item.uuid === object.uuid);
      if (index === -1) dragObjects.push(object);
    }
  }

  removeFromDrag(objects: THREE.Object3D[]): void {
    const dragObjects = this.dragControls.objects;
    for (const object of objects) {
      const index = dragObjects.findIndex((item) => item.uuid === object.uuid);
      if (index > -1) dragObjects.splice(index, 1);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  dragStart(event: any): void {
    if (this.troisRenderer && this.troisRenderer.three.cameraCtrl) this.troisRenderer.three.cameraCtrl.enabled = false;

    this.dragObject = event.object;
    this.dragCurve = this.getCurveForMeshPoint(event.object);
  }

  dragEnd(): void {
    if (this.selectionList.getSelectedObject()) this.lastUpdateTime = Date.now();

    if (this.curvePointsChanged && this.historyList && this.activeCurve) {
      const newPoints = this.activeCurve.pointList.map((point) => point.position.clone());
      const oldPoints = this.oldCurvePoints.map((point) => point.clone());
      const activeCurve = this.activeCurve;
      const targetId = (this.activeCurve.target as any).apiData.uuid;
      this.historyList.add(
        targetId,
        HistoryOperationType.curvePoints,
        async () => {
          this.undoRedoCurvePoints(activeCurve, oldPoints);
        },
        async () => {
          this.undoRedoCurvePoints(activeCurve, newPoints);
        }
      );
    }

    if (this.troisRenderer && this.troisRenderer.three.cameraCtrl) this.troisRenderer.three.cameraCtrl.enabled = true;

    this.dragObject = null;
    this.dragCurve = null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  drag(event: any): void {
    this.dragMesh(event.object);
    this.curvePointsChanged = true;
  }

  dragMesh(meshPoint: THREE.Object3D): void {
    for (const curve of this.curveList) {
      let useThisCurve = false;
      for (let i = 0; i < curve.pointList.length; i++) {
        const point = curve.pointList[i];
        if (point.uuid === meshPoint.uuid) {
          curve.curve.points[i] = point.position;
          useThisCurve = true;
        }
      }
      if (useThisCurve) {
        CurveTools.displayCurve(curve);
      }
    }
  }
  //#endregion drag & drop

  //#region history
  undoRedoCurvePoints(curve: CurveDescription, points: THREE.Vector3[]): void {
    for (let i = points.length; i < curve.pointList.length; i++) {
      const deletePoint = curve.pointList[i];
      this.removeFromDrag([deletePoint]);
      const line = deletePoint.parent;
      if (line) {
        const linePointIndex = line.children.findIndex((item) => item.uuid === deletePoint.uuid);
        if (linePointIndex > -1) {
          line.children.splice(linePointIndex, 1);
        }
      }
    }

    curve.curve.points.length = points.length;
    curve.pointList.length = points.length;
    for (let i = 0; i < points.length; i++) {
      curve.curve.points[i] = points[i].clone();
      if (curve.pointList[i]) curve.pointList[i].position.copy(points[i].clone());
      else {
        const list = this.displayCurvePoints(curve.mesh, [points[i].clone()]);
        curve.pointList[i] = list[0];
      }
    }

    CurveTools.displayCurve(curve);
  }
  //#endregion history

  //#region display curve
  displayCurvePoints(curveGroup: THREE.Object3D, curvePoints: THREE.Vector3[]): THREE.Object3D[] {
    const pointList: THREE.Object3D[] = [];
    for (const point of curvePoints) {
      const ctrl_pt_mesh = new THREE.Mesh(this.ctrl_pt_geom, this.ctrl_pt_material);
      ctrl_pt_mesh.position.copy(point);
      (ctrl_pt_mesh.material as any).ambient = ctrl_pt_mesh.material.color;
      ctrl_pt_mesh.name = SpecialObjectName.CurvePoint;
      curveGroup.add(ctrl_pt_mesh);
      pointList.push(ctrl_pt_mesh);
    }
    this.addToDrag(pointList);
    return pointList;
  }

  private static displayCurve(curve: CurveDescription): void {
    if (curve.curvePoints.length > 1) {
      curve.curve.updateArcLengths();
      const curveLength = Math.ceil(curve.curve.getLength());
      curve.length = curveLength;
      curve.pointCount = curveLength < minCurvePoints ? minCurvePoints : curveLength;
    }
    const points = curve.curve.points.length > 1 ? curve.curve.getPoints(curve.pointCount) : [];
    curve.geometry.setFromPoints(points);
  }

  private getCurveForMeshPoint(meshPoint: THREE.Object3D): CurveDescription | null {
    for (const curve of this.curveList) {
      for (let i = 0; i < curve.pointList.length; i++) {
        const point = curve.pointList[i];
        if (point.uuid === meshPoint.uuid) {
          return curve;
        }
      }
    }
    return null;
  }

  private getCurveForLine(line: THREE.Object3D): CurveDescription | null {
    for (const curve of this.curveList) {
      if (curve.mesh.uuid === line.uuid) return curve;
    }
    for (const child of line.children) {
      const childCurve = this.getCurveForLine(child);
      if (childCurve) return childCurve;
    }
    return null;
  }
  //#endregion display curve

  //#region transform curve
  curvePlaneSelected(): boolean {
    const selection = this.selectionList.getSelectedObject();
    return this.isCurvePlane(selection);
  }

  isCurvePlane(object: THREE.Object3D | null | undefined): boolean {
    const apiData = (object as any)?.apiData;
    return !!object && !!apiData && apiData.name.startsWith(SpecialObjectPrefix.plane);
  }

  @Watch('canCurve', { immediate: true })
  onCanCurveChanged(): void {
    this.selectedObjectChanged();
    this.onTransformToolsChanged();
    const curveItem = this.modelValue.getItemByName((this as any).$t('enum.upload-category.curve'));
    if (curveItem) {
      curveItem.active = this.canCurve;
      this.modelValue.updateStructure();
    }
    this.activatedCategoryTypes();
  }

  @Watch('allowedCurveCategories', { immediate: true, deep: true })
  activatedCategoryTypes(): void {
    if (this.allowedCurveCategories) {
      this.curveData.category = this.getDefaultCategory();
      for (const category of Object.values(CurveCategory)) {
        const isActive = this.allowedCurveCategories.includes(category) && this.canCurve;
        const categoryItem = this.modelValue.getItemByName((this as any).$t(`enum.curve-category.${category}`));
        if (categoryItem) {
          categoryItem.active = isActive;
        }
      }
      const curveItem = this.modelValue.getItemByName((this as any).$t('enum.upload-category.curve'));
      if (curveItem) {
        curveItem.updateActiveChildren();
        this.modelValue.updateStructure();
      }
    }
  }

  getDefaultCategory(): CurveCategory {
    return this.allowedCurveCategories && this.allowedCurveCategories.length > 0 && !this.allowedCurveCategories.includes(CurveCategory.ZIP)
      ? this.allowedCurveCategories[0]
      : CurveCategory.ZIP;
  }

  isCurvePlaneSelected = false;
  selectedObjectChanged(): void {
    if (this.canCurve) {
      const selection = this.selectionList.getSelectedObject();
      if (selection) {
        this.activeCurve = this.getCurveForLine(selection);
      } else {
        this.activeCurve = null;
      }
      this.isCurvePlaneSelected = this.curvePlaneSelected();
      const scale = this.activeCurve?.mesh.parent?.scale.x ?? 1;
      this.ctrl_pt_geom = new THREE.SphereGeometry(this.getPointRadius() / scale);
      setTimeout(() => {
        if (this.transformTools) {
          this.transformTools.setTransformControlsForTarget(this.isCurvePlaneSelected ? this.selectionList.getSelectedObject() : null);
        }
      }, 100);
    }
  }

  @Watch('transformTools.isModifyTypeNone', { immediate: true })
  onTransformToolsChanged(): void {
    if (this.canEditCurve()) {
      this.isModifyTypeMove = this.isCurveTool(CurveTool.move);
      this.isModifyTypeAdd = this.isCurveTool(CurveTool.add);
      this.isModifyTypeRemove = this.isCurveTool(CurveTool.remove);
    } else {
      this.deactivateDrag();
      this.isModifyTypeMove = false;
      this.isModifyTypeAdd = false;
      this.isModifyTypeRemove = false;
    }
  }

  saveTransform(): void {
    //
  }

  public calculateResult(data: MeshTreeDataItem): void {
    const curve = this.curveList.find((item) => item.mesh.uuid === data.mesh.uuid);
    if (curve) {
      this.projectCurve(curve, data);
    }
  }

  projectCurve(curve: CurveDescription, treeItem: MeshTreeDataItem): void {
    if (!this.canCalculateResult) return;

    curve.curvePointsProjected = [];
    curve.curvePointsProjectedBack = [];

    if (curve.target.name.startsWith('plane:') && curve.projectionObject) {
      this.projectPlanePoints(curve);
    } else {
      // If no projection is needed, use the raw curve points
      curve.curvePointsProjected = [...curve.curve.points];
      curve.curvePointsProjectedBack = [...curve.curve.points];
    }

    this.calculateZip(curve, treeItem);
    this.calculateCrop(curve, treeItem);
  }

  private projectPlanePoints = (curve: CurveDescription) => {
    const rayCaster = new THREE.Raycaster();
    curve.projectionObject?.updateWorldMatrix(false, false);

    const getDirection = (tryCount: number): THREE.Vector3 => {
      const direction = curve.mesh.getWorldDirection(new THREE.Vector3(0, 0, 1)).normalize();
      if ((tryCount === 0 && curve.projectionDirection === 1) || (tryCount === 1 && curve.projectionDirection === -1)) {
        return direction;
      }
      return direction.clone().negate();
    };

    const checkIntersection = (point: THREE.Vector3, direction: THREE.Vector3): THREE.Vector3 | null => {
      if (curve.projectionObject) {
        rayCaster.set(point, direction);
        const intersection = rayCaster.intersectObject(curve.projectionObject, false);
        if (intersection.length > 0) return intersection[0].point;
      }
      return null;
    };

    curve.projectionDirection = 1;

    for (const point of curve.curve.points) {
      const worldPoint = curve.mesh.localToWorld(point.clone());
      let intersectionPoint = checkIntersection(worldPoint, getDirection(0));

      if (!intersectionPoint) {
        intersectionPoint = checkIntersection(worldPoint, getDirection(1));
        curve.projectionDirection = -1;
      }

      if (intersectionPoint) {
        curve.curvePointsProjected.push(intersectionPoint);
        const backIntersectionPoint = checkIntersection(intersectionPoint.clone().add(getDirection(0).clone().multiplyScalar(100000)), getDirection(1));
        if (backIntersectionPoint) {
          curve.curvePointsProjectedBack.push(backIntersectionPoint);
        }
      }
    }
  };

  deleteCurve(treeItem: MeshTreeDataItem): void {
    const parent = this.modelValue.getParent(treeItem);
    if (treeItem) this.modelValue.removeItem(treeItem);
    if (parent && parent.mesh) this.modelValue.removeItem(parent);
  }

  async calculateZip(curve: CurveDescription, treeItem: MeshTreeDataItem): Promise<void> {
    if (curve.info.category !== CurveCategory.ZIP || !this.canCalculateResult) {
      return;
    }
    const reference = curve.projectionObject ?? curve.target;
    // Use curve.curvePointsProjected which is now correctly populated
    if (curve.curvePointsProjected.length > 1 && reference) {
      const vertexRadiusCount = 20;
      const boundingBox = THREEBoundingBox.getBoundingBox([reference]);
      const boxSize = boundingBox.getSize(new THREE.Vector3());
      const path = new THREE.CatmullRomCurve3(curve.curvePointsProjected, curve.info.closed, curve.info.type);
      const radius = Math.min(boxSize.x * reference.scale.x, boxSize.z * reference.scale.z) / 20;
      const geometry = new THREE.TubeGeometry(path, (curve.curvePointsProjected.length - 1) * 3, radius, vertexRadiusCount - 1, false);

      const positionAttribute = geometry.getAttribute('position');
      const vertex = new THREE.Vector3();
      const distanceToPlane: number[] = [];
      const direction = curve.target.getWorldDirection(new THREE.Vector3(0, 0, 1)).normalize();
      const plane = new THREE.Plane(direction, curve.target.position.length());
      for (let i = 0; i < vertexRadiusCount; i++) {
        vertex.fromBufferAttribute(positionAttribute, i);
        distanceToPlane.push(plane.distanceToPoint(vertex));
      }
      let minIndex = 0;
      let maxIndex = 0;
      for (let i = 0; i < vertexRadiusCount; i++) {
        if (distanceToPlane[i] < distanceToPlane[minIndex]) minIndex = i;
        if (distanceToPlane[i] > distanceToPlane[maxIndex]) maxIndex = i;
      }
      const uvs = geometry.attributes.uv as THREE.Float32BufferAttribute;
      const uvCoordsOriginal = [...Array.from(uvs.array)];
      const uvCoordsNew = [...uvCoordsOriginal];
      for (let i = 0; i < uvs.count; i++) {
        uvCoordsNew[i * 2] = uvCoordsOriginal[i * 2 + 1];
        uvCoordsNew[i * 2 + 1] = uvCoordsOriginal[i * 2];
      }
      const uvCoordsRotated = [...uvCoordsNew];
      let shiftCount = (2 * vertexRadiusCount - maxIndex - 1) % vertexRadiusCount;
      if (curve.projectionDirection === -1) shiftCount = (2 * vertexRadiusCount - minIndex - 1) % vertexRadiusCount;
      for (let i = 0; i < uvs.count; i++) {
        const row = Math.floor(i / vertexRadiusCount);
        const column = i % vertexRadiusCount;
        const columnRotated = (column + shiftCount + vertexRadiusCount) % vertexRadiusCount;
        const iRotated = row * vertexRadiusCount + columnRotated;
        uvCoordsRotated[i * 2] = uvCoordsNew[iRotated * 2];
      }
      uvs.set(uvCoordsRotated);
      uvs.needsUpdate = true;

      if (curve.calculatedObject) {
        curve.calculatedObject.geometry = geometry;
      } else {
        new THREE.TextureLoader().load('assets/bump/zip.jpg', (bmap) => {
          const material = new THREE.MeshPhongMaterial({
            color: 0xffd400,
            shininess: 20,
            bumpMap: bmap,
            bumpScale: radius,
          });
          curve.calculatedTreeData = this.modelValue.addObject(
            `zip:${curve.info.name}`,
            THREEEnum.ObjectType.Mesh,
            geometry,
            material,
            new THREE.Vector3(0, 0, 0),
            new THREE.Euler(0, 0, 0),
            new THREE.Vector3(1, 1, 1),
            this.$t('enum.upload-category.calculationParts'),
            false
          );
          setTimeout(() => {
            if (curve.calculatedTreeData) curve.calculatedObject = curve.calculatedTreeData.mesh as THREE.Mesh;
            if (treeItem) this.deleteCurve(treeItem);
            const index = this.curveList.findIndex((item) => item.info.curveId);
            if (index > -1) this.curveList.splice(index, 1);
          }, 100);
        });
      }
    }
  }

  async calculateCrop(curve: CurveDescription, treeItemCurve: MeshTreeDataItem): Promise<void> {
    if ((curve.info.category !== CurveCategory.CROP && curve.info.category !== CurveCategory.SPACER) || !this.canCalculateResult) {
      return;
    }

    const reference = curve.projectionObject ?? curve.target;
    if (curve.curve.points.length <= 2 || !reference) {
      return;
    }

    let vertexRadiusCount = Math.max(20, curve.curve.points.length * 2);
    let geometry = new THREE.CylinderGeometry();
    const isPlaneProjection = curve.target.name.startsWith('plane:') && curve.projectionObject;
    const worldPoints = curve.curve.getPoints(vertexRadiusCount - 1).map((point) => curve.mesh.localToWorld(point.clone()));
    const direction = this.calculateNormal(worldPoints);
    const getDirection = (tryCount: number): THREE.Vector3 => {
      return (tryCount === 0 && curve.projectionDirection === 1) || (tryCount === 1 && curve.projectionDirection === -1)
          ? direction
          : direction.clone().negate();
    };
    const center = this.calculateCenter(worldPoints);
    const boundingBox = THREEBoundingBox.getBoundingBox([reference]);
    const boxSize = boundingBox.getSize(new THREE.Vector3()).clone().multiply(reference.scale);
    let extrudeLength = Math.min(boxSize.x, boxSize.z) * 0.3;
    let extrudeLength2 = Math.max(boxSize.x, boxSize.z) * 0.5;

    if (curve.curvePointsProjectedBack.length > 0 && isPlaneProjection) {
      const plane = new THREE.Plane(direction, curve.target.position.length());
      const centerDistance = plane.distanceToPoint(center);
      const distanceToPlane = curve.curvePointsProjectedBack.map(point => Math.abs(plane.distanceToPoint(point) - centerDistance));
      extrudeLength = Math.max(...distanceToPlane) * 1.3;
      extrudeLength2 = 0;
    }

    let distanceVector = getDirection(0).multiplyScalar(extrudeLength);
    let distanceVector2: THREE.Vector3 | null = null;

    if (!isPlaneProjection) {
      distanceVector = this.calculateNormal(worldPoints).multiplyScalar(extrudeLength);
      distanceVector2 = this.calculateNormal(worldPoints).multiplyScalar(extrudeLength2).negate();
    }

    const radius = this.calculateMaxDistance(worldPoints, center);
    geometry = new THREE.CylinderGeometry(radius, radius, extrudeLength, vertexRadiusCount);
    this.adaptVertexPosition(geometry, worldPoints, distanceVector, distanceVector2, center);

    if (curve.calculatedObject) {
      curve.calculatedObject.geometry = geometry;
      this.subtractMesh(reference, curve.calculatedObject, curve.calculatedTreeData, curve, treeItemCurve, this.curveList, this.modelValue, this.deleteCurve);
    } else {
      curve.calculatedTreeData = this.modelValue.addObject(
        `calculationParts:${curve.info.name}`,
        THREEEnum.ObjectType.Mesh,
        geometry,
        new THREE.MeshLambertMaterial({ color: 0xffd400, side: THREE.DoubleSide }),
        new THREE.Vector3(0, 0, 0),
        new THREE.Euler(0, 0, 0),
        new THREE.Vector3(1, 1, 1),
        this.$t('enum.upload-category.calculationParts'),
        false
      );
      setTimeout(() => {
        if (curve.calculatedTreeData) {
          curve.calculatedObject = curve.calculatedTreeData.mesh as THREE.Mesh;
          this.subtractMesh(reference, curve.calculatedObject, curve.calculatedTreeData, curve, treeItemCurve, this.curveList, this.modelValue, this.deleteCurve);
        }
      }, 100);
    }
  }

  private subtractMesh = (target: THREE.Object3D, subtract: THREE.Object3D, treeItem: MeshTreeDataItem | null, curve: CurveDescription, treeItemCurve: MeshTreeDataItem, curveList: CurveDescription[], modelValue: any, deleteCurve: (item: MeshTreeDataItem) => void): THREE.Object3D => {
    target.updateMatrix();
    subtract.updateMatrix();
    if (curve.info.category === CurveCategory.CROP) {
      const subRes = CSG.subtract(target as THREE.Mesh, subtract as THREE.Mesh);
      (target as THREE.Mesh).geometry = subRes.geometry;
    } else {
      const subRes = CSG.intersect(target as THREE.Mesh, subtract as THREE.Mesh);
      (target as THREE.Mesh).geometry = subRes.geometry;
    }
    if (treeItem) modelValue.removeItem(treeItem);
    if (treeItemCurve) deleteCurve(treeItemCurve);
    const index = curveList.findIndex((item) => item.info.curveId);
    if (index > -1) curveList.splice(index, 1);
    return target;
  };

  private calculateCenter = (points: THREE.Vector3[]): THREE.Vector3 => {
    const center = new THREE.Vector3(0, 0, 0);
    for (const point of points) {
      center.add(point);
    }
    center.divideScalar(points.length);
    return center;
  };

  private calculateNormal = (points: THREE.Vector3[]): THREE.Vector3 => {
    const normal = new THREE.Vector3(0, 0, 0);
    for (let i = 0; i < points.length; i++) {
      const next = points[(i + 1) % points.length];
      normal.x += (points[i].y - next.y) * (points[i].z + next.z);
      normal.y += (points[i].z - next.z) * (points[i].x + next.x);
      normal.z += (points[i].x - next.x) * (points[i].y + next.y);
    }
    normal.normalize();
    return normal;
  };

  private calculateMaxDistance = (points: THREE.Vector3[], center: THREE.Vector3): number => {
    let maxDistance = 0;
    for (const point of points) {
      const distance = point.distanceTo(center);
      if (distance > maxDistance) maxDistance = distance;
    }
    return maxDistance;
  };

  private getRowDistance = (row: number, distanceVector: THREE.Vector3, distanceVector2: THREE.Vector3 | null): THREE.Vector3 => {
    if (row === 1) return distanceVector;
    if (distanceVector2) {
      return distanceVector2;
    }
    return new THREE.Vector3(0, 0, 0);
  };

  private adaptVertexPosition = (geometry: THREE.BufferGeometry, points: THREE.Vector3[], distanceVector: THREE.Vector3, distanceVector2: THREE.Vector3 | null, center: THREE.Vector3): void => {
    const positionAttribute = geometry.getAttribute('position');
    const rowCount = points.length + 1;

    for (let i = 0; i < rowCount * 2; i++) {
      const row = Math.floor(i / rowCount);
      const column = i % points.length;
      const columnPoint = points[column];
      const rowDistance = this.getRowDistance(row, distanceVector, distanceVector2);
      positionAttribute.setXYZ(i, columnPoint.x + rowDistance.x, columnPoint.y + rowDistance.y, columnPoint.z + rowDistance.z);
    }
    const capCount = points.length * 2 + 1;
    for (let i = rowCount * 2; i < positionAttribute.count; i++) {
      const index = i - rowCount * 2;
      const row = Math.floor(index / capCount);
      const capIndex = index % capCount;
      const isCenter = capIndex < points.length;
      const column = capIndex % points.length;
      const columnPoint = points[column];
      const rowDistance = this.getRowDistance(row, distanceVector, distanceVector2);

      if (isCenter) {
        positionAttribute.setXYZ(i, center.x + rowDistance.x, center.y + rowDistance.y, center.z + rowDistance.z);
      } else {
        positionAttribute.setXYZ(i, columnPoint.x + rowDistance.x, columnPoint.y + rowDistance.y, columnPoint.z + rowDistance.z);
      }
    }
    positionAttribute.needsUpdate = true;
  };
  //#endregion transform curve

  //#region import / export
  exportCurves(): CurveExportData[] {
    return this.curveList.map((data) => {
      return {
        curveId: data.info.curveId,
        category: data.info.category,
        name: data.info.name,
        closed: data.info.closed,
        type: data.info.type,
        meshId: data.target.name.startsWith(SpecialObjectPrefix.plane) ? null : (data.target as any).apiData.id,
        position: data.target.position.clone(),
        rotation: data.target.rotation.clone(),
        scale: data.target.scale.clone(),
        curvePoints: data.curvePoints,
      };
    });
  }

  async importCurves(data: CurveExportData[]): Promise<void> {
    for (const item of data) {
      this.curveData.curveId = item.curveId;
      this.curveData.category = item.category;
      this.curveData.name = item.name;
      this.curveData.closed = item.closed;
      this.curveData.type = item.type;
      let curveDescription: CurveDescription | null = null;
      if (item.meshId) {
        const selection = this.troisRenderer.scene?.children.find((sceneItem: any) => {
          if ((sceneItem as any).apiData) return (sceneItem as any).apiData.id === item.meshId;
          return false;
        });
        if (selection) curveDescription = await this.addCurve(selection, this.getItemCategory(), selection, selection);
      } else {
        curveDescription = await this.addPlaneCurve();
      }
      if (curveDescription) {
        if (!item.meshId) {
          curveDescription.target.position.copy(item.position.clone());
          curveDescription.target.rotation.copy(item.rotation.clone());
          curveDescription.target.scale.copy(item.scale.clone());
        }
        curveDescription.curve.points.push(...item.curvePoints);
        const list = this.displayCurvePoints(curveDescription.mesh, item.curvePoints);
        curveDescription.pointList.push(...list);
        CurveTools.displayCurve(curveDescription);
      }
    }
    this.onCanCurveChanged();
  }
  //#endregion import / export
}
</script>

<style lang="scss" scoped>
.active {
  color: var(--el-color-primary);
}

.level-item:not(:last-child) {
  display: flex;
  align-items: center;
  margin-right: 0.6rem;
  margin-bottom: 0;
}
</style>
